/**
 * Single source of truth for the frontend API client.
 *
 * Behaviour, applied to every request:
 * - Cookie-based auth (`credentials: "include"`)
 * - Auto-injects `X-CSRF-Token` (mutating methods) read from the cookie
 * - Auto-injects `X-Organization-Id` from cached localStorage
 * - Silent token refresh + retry once on 401 (skipped for `/auth/*`)
 * - Standard `{ success, data, error, meta }` envelope handling
 *
 * Two ergonomics, same plumbing:
 * - Typed methods: `apiGet/apiPost/apiPut/apiPatch/apiDelete/apiUpload` —
 *   return the full `ApiEnvelope<T>`. Prefer this for new code.
 * - Legacy shims: `apiFetch` / `apiFetchWithMeta` — return just the data
 *   (or `{ data, meta }`). Kept so `lib/api.ts` callers keep working.
 */
import { ApiError, NetworkError } from "./errors";
import { reportError } from "./logger";
import { COOKIE_CSRF, LEGACY_COOKIE_CSRF } from "./cookieNames";

export { ApiError } from "./errors";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiEnvelope<T> {
  readonly success: boolean;
  readonly data?: T;
  readonly error?: string;
  readonly meta?: {
    readonly total?: number;
    readonly page?: number;
    readonly limit?: number;
  };
}

/** Backwards-compat alias for the `lib/api.ts` `ApiResponse<T>` type. */
export type ApiResponse<T> = ApiEnvelope<T>;

export interface ApiRequestOptions {
  readonly params?: Record<string, string | number | boolean | undefined>;
  readonly headers?: Record<string, string>;
  readonly signal?: AbortSignal;
}

export interface ApiResult<T> {
  readonly data: T;
  readonly meta?: ApiEnvelope<T>["meta"];
}

// ---------------------------------------------------------------------------
// URL & cookie helpers
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1";

function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function getCookie(name: string): string | undefined {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? match.substring(name.length + 1) : undefined;
}

/**
 * Wipe the (non-HttpOnly) csrf cookie on host-only and parent-domain scopes.
 * Called when the server has signalled that the session is dead and we want
 * a clean slate before redirecting to /login. The HttpOnly access / refresh
 * tokens are wiped server-side by /auth/logout or /auth/refresh on failure;
 * we mirror that on the client for the one cookie JS can see, so
 * AuthContext's mount-time csrf-cookie short-circuit doesn't keep the dead
 * session alive in memory.
 *
 * Wipes both the current `dhze_csrf_token` and the legacy `csrf-token` so a
 * mid-rollout browser doesn't keep a stale legacy cookie shadowing the new one.
 */
function clearCsrfCookie(): void {
  const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  document.cookie = `${COOKIE_CSRF}=; ${expire}`;
  document.cookie = `${LEGACY_COOKIE_CSRF}=; ${expire}`;
  const parts = window.location.hostname.split(".");
  if (parts.length >= 2) {
    const parentDomain = parts.slice(-2).join(".");
    document.cookie = `${COOKIE_CSRF}=; ${expire}; domain=.${parentDomain}`;
    document.cookie = `${LEGACY_COOKIE_CSRF}=; ${expire}; domain=.${parentDomain}`;
  }
}

// ---------------------------------------------------------------------------
// Organization context cache (debounces localStorage reads)
// ---------------------------------------------------------------------------

const ORG_STORAGE_KEY = "denhartog-ze-selected-org";
const ORG_CACHE_TTL_MS = 1000;

const orgIdCache = (() => {
  let cached: string | null = null;
  let lastReadAt = 0;

  return {
    get: (): string | null => {
      const now = Date.now();
      if (now - lastReadAt > ORG_CACHE_TTL_MS) {
        cached = localStorage.getItem(ORG_STORAGE_KEY);
        lastReadAt = now;
      }
      return cached;
    },
    invalidate: (): void => {
      lastReadAt = 0;
    },
  };
})();

/**
 * Invalidate the cached organization ID so the next request re-reads
 * localStorage. Call after switching organizations for immediate effect.
 */
export const invalidateOrgIdCache = orgIdCache.invalidate;

// ---------------------------------------------------------------------------
// Token refresh — proactive (before expiry) + reactive (on 401)
// ---------------------------------------------------------------------------

/** Must match backend `config.cookie.accessTokenMaxAge` (15 minutes). */
const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000;
/** Refresh this many ms before expiry to avoid a 401 round-trip. */
const REFRESH_BUFFER_MS = 60 * 1000;

let tokenExpiresAt = 0;

/**
 * Mark the access token as freshly set. Call after every action that results
 * in new auth cookies (login, 2FA verify, mount-time `/auth/me` success,
 * silent refresh). Drives the proactive-refresh check below.
 */
export function markTokenFresh(): void {
  tokenExpiresAt = Date.now() + ACCESS_TOKEN_LIFETIME_MS - REFRESH_BUFFER_MS;
}

function shouldProactivelyRefresh(): boolean {
  // Unknown token age (fresh page load before AuthContext has run, or after
  // logout) — leave the reactive 401 → refresh → retry flow to handle it.
  if (tokenExpiresAt === 0) return false;
  return Date.now() >= tokenExpiresAt;
}

let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) markTokenFresh();
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** @internal Reset module state between tests. */
export function _resetForTesting(): void {
  tokenExpiresAt = 0;
  refreshPromise = null;
  orgIdCache.invalidate();
}

// ---------------------------------------------------------------------------
// Request building
// ---------------------------------------------------------------------------

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function buildInit(
  method: string,
  options: ApiRequestOptions,
  body?: BodyInit,
): RequestInit {
  const headers: Record<string, string> = {};

  // Browsers set the multipart boundary themselves for FormData — leave
  // Content-Type unset in that case.
  if (!(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  Object.assign(headers, options.headers);

  if (MUTATING_METHODS.has(method)) {
    const csrfToken = getCookie(COOKIE_CSRF);
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  }

  const orgId = orgIdCache.get();
  if (orgId) headers["X-Organization-Id"] = orgId;

  return {
    method,
    credentials: "include",
    cache: "no-store",
    headers,
    signal: options.signal,
    body,
  };
}

// ---------------------------------------------------------------------------
// Response handling
// ---------------------------------------------------------------------------

async function handleResponse<T>(res: Response): Promise<ApiEnvelope<T>> {
  if (res.status === 204 || res.status === 304) {
    return { success: res.ok } as ApiEnvelope<T>;
  }

  let body: ApiEnvelope<T>;
  try {
    body = await res.json();
  } catch {
    throw new ApiError("Ongeldig antwoord van server", res.status);
  }

  if (!res.ok || !body.success) {
    const error = new ApiError(
      body.error ?? "Er ging iets mis",
      res.status,
      undefined,
      body.data,
    );
    // Only report genuine server failures. 4xx (auth, validation, not-found)
    // is expected control flow — the caller handles it.
    if (res.status >= 500) {
      reportError(error, { extra: { path: res.url, status: res.status } });
    }
    throw error;
  }

  return body;
}

/**
 * Endpoints that manage their own credentials — never trigger a refresh
 * round-trip on 401. `/auth/me` is intentionally NOT in this list: a 401 on
 * `/auth/me` is the canonical signal that the access token is dead, and the
 * refresh-then-retry dance is exactly what we want there.
 */
const AUTH_NO_REFRESH_RE =
  /\/auth\/(?:login|register|refresh|verify-2fa|verify-2fa-backup|logout)(?:$|\?)/;

/**
 * Hard-redirect to /login after a refresh attempt has definitively failed.
 * Mirrors the ERE pattern: refresh-failure means the server no longer
 * recognises this session (token rotated, user deleted by re-seed, secret
 * rotated by deploy). Wipe the JS-readable csrf-token cookie so AuthContext's
 * mount-time short-circuit doesn't keep the dead session alive, then bounce
 * to /login. The HttpOnly cookies were already wiped server-side by /auth/refresh.
 */
function redirectToLoginOnDeadSession(): void {
  clearCsrfCookie();
  if (
    typeof window !== "undefined" &&
    window.location?.pathname !== "/login"
  ) {
    window.location.href = "/login";
  }
}

/**
 * Fetch wrapper with proactive + reactive token refresh.
 *
 * Proactive: if the access token is past its refresh-buffer threshold, kick
 * off a refresh before the request leaves. Eliminates the 401 → refresh →
 * retry round-trip for the common case.
 *
 * Reactive: if a 401 still slips through, attempt one refresh and retry. On
 * refresh-failure (server doesn't know us anymore), redirect to /login so the
 * UI doesn't get stuck rendering "Ongeldig token" toasts forever.
 *
 * Both paths are skipped for `/auth/login|register|refresh|verify-2fa*|logout`
 * — those manage tokens themselves and a 401 there means bad credentials.
 */
async function fetchWithRefresh(
  buildRequest: () => { url: string; init: RequestInit },
): Promise<Response> {
  const initial = buildRequest();
  const isAuthEndpoint = AUTH_NO_REFRESH_RE.test(initial.url);

  if (!isAuthEndpoint && shouldProactivelyRefresh()) {
    await attemptTokenRefresh();
    const proactive = buildRequest();
    return fetch(proactive.url, proactive.init);
  }

  const res = await fetch(initial.url, initial.init);

  if (res.status !== 401) return res;
  if (isAuthEndpoint) return res;

  const refreshed = await attemptTokenRefresh();
  if (!refreshed) {
    redirectToLoginOnDeadSession();
    return res;
  }

  const retry = buildRequest();
  return fetch(retry.url, retry.init);
}

// ---------------------------------------------------------------------------
// Public typed API
// ---------------------------------------------------------------------------

export async function apiGet<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  try {
    const res = await fetchWithRefresh(() => ({
      url: buildUrl(path, options.params),
      init: buildInit("GET", options),
    }));
    return handleResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new NetworkError();
  }
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  try {
    const res = await fetchWithRefresh(() => ({
      url: buildUrl(path, options.params),
      init: buildInit(
        "POST",
        options,
        body !== undefined ? JSON.stringify(body) : undefined,
      ),
    }));
    return handleResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new NetworkError();
  }
}

export async function apiPut<T>(
  path: string,
  body?: unknown,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  try {
    const res = await fetchWithRefresh(() => ({
      url: buildUrl(path, options.params),
      init: buildInit(
        "PUT",
        options,
        body !== undefined ? JSON.stringify(body) : undefined,
      ),
    }));
    return handleResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new NetworkError();
  }
}

export async function apiPatch<T>(
  path: string,
  body?: unknown,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  try {
    const res = await fetchWithRefresh(() => ({
      url: buildUrl(path, options.params),
      init: buildInit(
        "PATCH",
        options,
        body !== undefined ? JSON.stringify(body) : undefined,
      ),
    }));
    return handleResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new NetworkError();
  }
}

export async function apiDelete<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  try {
    const res = await fetchWithRefresh(() => ({
      url: buildUrl(path, options.params),
      init: buildInit("DELETE", options),
    }));
    return handleResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new NetworkError();
  }
}

/**
 * Uploads a multipart/form-data POST. The browser sets the boundary, so we
 * never pass Content-Type ourselves.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  try {
    const res = await fetchWithRefresh(() => ({
      url: buildUrl(path, options.params),
      init: buildInit("POST", options, formData),
    }));
    return handleResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new NetworkError();
  }
}

// ---------------------------------------------------------------------------
// Legacy compat shims — return data (or { data, meta }) directly.
// Prefer the typed methods above for new code.
// ---------------------------------------------------------------------------

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const result = await apiFetchWithMeta<T>(path, options);
  return result.data;
}

export async function apiFetchWithMeta<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResult<T>> {
  const method = (options.method ?? "GET").toUpperCase();
  const reqOptions: ApiRequestOptions = {
    headers: options.headers as Record<string, string> | undefined,
    signal: options.signal ?? undefined,
  };
  const parsedBody =
    typeof options.body === "string" && options.body.length > 0
      ? (JSON.parse(options.body) as unknown)
      : undefined;

  let envelope: ApiEnvelope<T>;
  switch (method) {
    case "GET":
      envelope = await apiGet<T>(path, reqOptions);
      break;
    case "POST":
      envelope = await apiPost<T>(path, parsedBody, reqOptions);
      break;
    case "PUT":
      envelope = await apiPut<T>(path, parsedBody, reqOptions);
      break;
    case "PATCH":
      envelope = await apiPatch<T>(path, parsedBody, reqOptions);
      break;
    case "DELETE":
      envelope = await apiDelete<T>(path, reqOptions);
      break;
    default:
      throw new Error(`Unsupported method: ${method}`);
  }

  return { data: envelope.data as T, meta: envelope.meta };
}
